'use client'

import { LoaderCircle } from 'lucide-react'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  HoverCardPortal,
} from '@/components/ui/hover-card'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { ISuperPromptModel } from '@/lib/db/types'
import Image from 'next/image'
import { FormilySchemaRenderer } from '@/components/formily/renderer'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'

interface SuperPromptItemProps {
  prompt: ISuperPromptModel
  isLoading?: boolean
  onClick?: (prompt: ISuperPromptModel) => void
  className?: string
  hoverCardSide?: 'right' | 'left' | 'top' | 'bottom'
  hoverCardSideOffset?: number
  hoverCardClassName?: string
}

export function SuperPromptItem({
  prompt,
  isLoading = false,
  onClick,
  className,
  hoverCardSide = 'right',
  hoverCardSideOffset = 16,
  hoverCardClassName,
}: SuperPromptItemProps) {
  const { dt } = useDynamicI18n()

  return (
    <HoverCard openDelay={0} closeDelay={0}>
      <HoverCardTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-3 px-3 py-3 hover:bg-white/10 cursor-pointer transition-colors group rounded-lg',
            isLoading && 'opacity-50 cursor-not-allowed',
            className,
          )}
          onClick={() => {
            if (isLoading) return
            onClick?.(prompt)
          }}
        >
          {/* Icon */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {prompt.logo ? (
              <img
                src={prompt.logo}
                alt={prompt.name}
                width={24}
                height={24}
                className="object-contain"
              />
            ) : (
              <div className="w-6 h-6 rounded-sm" />
            )}
          </div>

          {/* Name */}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate transition-colors">
              {prompt.name}
            </p>
          </div>

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex-shrink-0">
              <LoaderCircle className="w-4 h-4 animate-spin text-white/60" />
            </div>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side={hoverCardSide}
          align="start"
          className={cn('w-80', hoverCardClassName)}
          sideOffset={hoverCardSideOffset}
          avoidCollisions={false}
          collisionPadding={0}
        >
          <Card className="border-none shadow-none">
            <CardHeader className="flex flex-col gap-3 p-4">
              {/* First row: icon + name */}
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center">
                  {prompt.logo ? (
                    <img
                      src={prompt.logo}
                      alt={prompt.name}
                      width={24}
                      height={24}
                      className="object-contain"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-sm" />
                  )}
                </div>
                <CardTitle className="text-white text-base font-semibold leading-tight">
                  {prompt.name}
                </CardTitle>
              </div>
              {/* Second row: description */}
              <CardDescription className="text-gray-300 text-sm leading-relaxed">
                {dt(prompt.description) || 'No description'}
              </CardDescription>

              {/* Third row: example image */}
              {prompt.example && (
                <div className="mt-2">
                  <div className="relative w-full h-40 bg-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden">
                    <Image
                      src={prompt.example}
                      alt={`${prompt.name} example`}
                      fill
                      className="object-cover transition-opacity duration-300"
                      sizes="(max-width: 768px) 100vw, 320px"
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                      loading="lazy"
                      onLoad={(e) => {
                        e.currentTarget.style.opacity = '1'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                      style={{
                        opacity: '0',
                      }}
                    />
                    {/* Simple loading indicator */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-300/10 to-gray-400/10 animate-pulse" />
                  </div>
                </div>
              )}
              {
                // Render CardContent if configured in dashboard
                prompt.parameters?.prompt &&
                  Object.values(
                    prompt.parameters?.prompt?.properties || {},
                  ).some((property: any) => property.type === 'void') && (
                    <CardContent className="p-0">
                      <FormilySchemaRenderer
                        formilySchema={prompt.parameters?.prompt}
                        previewMode={true}
                        needTranslation={true}
                        onlyRenderUiNodes={true}
                      />
                    </CardContent>
                  )
              }
            </CardHeader>
          </Card>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  )
}
